
import { computed, defineComponent, onBeforeMount, PropType, reactive } from 'vue'

// components
import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'
import TheSelect from '@/common/components/UI/TheSelect/index.vue'
import TheInput from '@/common/components/UI/TheInput/index.vue'
import TheTime from '@/common/components/UI/TheTime/index.vue'
import TheTextarea from '@/common/components/UI/TheTextarea/index.vue'
import Checkbox from '@/common/components/UI/Checkbox/index.vue'

//constants
import { PHONE_NUMBER_MASK } from '@/core/constants/masks.constants'

// realisations
import { pvz } from '@/core/realisations/pvz.realisation'
import { cities } from '@/core/realisations/cities.realisation'
import { users } from '@/core/realisations/users.realisation'

// classes
import { DataState } from '@/core/classes/DataState.class'

// types
import { PVZT } from '@/core/types/pvz.types'
import { UserT } from '@/core/types/users.types'
import { CompanyT } from '@/core/types/companies.types'
import { CityT } from '@/core/types/cities.types'

// validation
import useVuelidate from '@vuelidate/core'
import { useMaska } from '@/common/composable/useMaska'
import { requiredIf } from '@vuelidate/validators'

// composable
import useHelpers from '@/common/composable/useHelpers'
import Geocoder from '@/modules/CreateOrderModule/components/Geocoder/index.vue'
import useNotifications from '@/common/composable/useNotifications'
import { companies } from '@/core/realisations/companies.realisation'

export default defineComponent({
    props: {
        initialValue: {
            type: Object as PropType<PVZT>,
            default: null,
        },
        isUpdate: {
            type: Boolean,
            default: false,
        },
        isCreate: {
            type: Boolean,
            default: false,
        },
        isAddUser: {
            type: Boolean,
            default: false,
        },
    },
    components: {
        'page-title': PageTitle,
        'the-button': TheButton,
        'the-input': TheInput,
        'the-select': TheSelect,
        geocoder: Geocoder,
        'the-time': TheTime,
        'the-textarea': TheTextarea,
        checkbox: Checkbox,
    },
    emits: ['refresh'],
    setup(props, { emit }) {
        const { unmaskNumbers } = useMaska()
        const notifications = useNotifications()

        const state = reactive({
            is_loading: false,
            is_modal_visible: false,

            user_id: null as number | null,

            city_id: null as number | null,
            company_id: null as number | null,
            full_address: null as string | null,
            street: null as string | null,

            city: null as CityT | null,
            id: null as number | null,
            latitude: null as number | null,
            longitude: null as number | null,
            name: null as string | null,
            phone: null as string | null,

            work_time_from: null as string | null,
            work_time_until: null as string | null,
            description: null as string | null,
            is_active: 0,
        })

        const geocode_state = reactive({
            is_opened: false,
            address: null as string | null,
            street: null as string | null,
            latitude: null as number | null,
            longitude: null as number | null,
        })

        const data_state = reactive({
            companies: new DataState<CompanyT>({}),
            cities: new DataState<CityT>({}),
            users: new DataState<UserT>({}),
        })

        const AddPVZValidation = {
            // not isAddUser only
            name: {
                required: requiredIf(function () {
                    return !props.isAddUser
                }),
            },
            phone: {
                required: requiredIf(() => !props.isAddUser),
            },
            city_id: {
                required: requiredIf(() => !props.isAddUser),
            },
            company_id: {
                required: requiredIf(() => !props.isAddUser),
            },
            full_address: {
                required: requiredIf(() => !props.isAddUser),
            },
            work_time_from: {
                required: requiredIf(() => !props.isAddUser),
            },
            work_time_until: {
                required: requiredIf(() => !props.isAddUser),
            },
            // isAddUser only
            user_id: {
                required: requiredIf(() => props.isAddUser),
            },
        }

        const state_form$ = useVuelidate(AddPVZValidation, state)

        // methods

        const setPVZ = () => {
            if (props.isUpdate && props.initialValue) {
                state.id = props.initialValue.id
                state.name = props.initialValue.name
                state.phone = props.initialValue.phone
                state.city = props.initialValue.city
                state.full_address = props.initialValue.full_address

                state.latitude = Number(props.initialValue.latitude)
                state.longitude = Number(props.initialValue.longitude)

                state.city_id = props.initialValue.city.id
                state.company_id = props.initialValue.company_id

                state.work_time_from = props.initialValue.work_time_from
                state.work_time_until = props.initialValue.work_time_until
                state.description = props.initialValue.description
                state.is_active = props.initialValue.is_active
            }
        }

        const onDeletePVZ = async () => {
            if (!state.id) return

            const answer = confirm('Вы действительно хотите удалить ПВЗ?')
            if (!answer) return

            try {
                state.is_loading = true
                await pvz.deletePVZ(state.id)

                notifications.notify({
                    title: 'ПВЗ удален!',
                    type: 'success',
                })

                emit('refresh')
            } catch (error) {
                console.log(error)
                notifications.notifyError({
                    error,
                    title: 'При удалении ПВЗ произошла ошбика',
                })
            } finally {
                state.is_loading = false
            }
        }

        const onCancelGeocode = () => {
            geocode_state.is_opened = false
            geocode_state.address = null
            geocode_state.street = null
            geocode_state.latitude = null
            geocode_state.longitude = null
        }

        const emitCancel = () => {
            if (geocode_state.is_opened) {
                onCancelGeocode()
                return
            }

            emit('refresh')
        }

        const updatePVZInfo = async () => {
            state_form$.value.$touch()
            if (state_form$.value.$invalid) return

            if (
                !state.id ||
                !state.name ||
                !state.latitude ||
                !state.longitude ||
                !state.phone ||
                !state.city_id ||
                !state.full_address ||
                !state.work_time_from ||
                !state.work_time_until
            )
                return

            try {
                state.is_loading = true

                const params = {
                    id: state.id,
                    name: state.name,
                    latitude: state.latitude,
                    longitude: state.longitude,
                    phone: unmaskNumbers(state.phone),
                    city_id: state.city_id,
                    company_id: state.company_id,
                    full_address: state.full_address,
                    work_time_from: state.work_time_from,
                    work_time_until: state.work_time_until,
                    description: state.description,
                    is_active: state.is_active,
                }

                await pvz.updatePVZInfo(params)

                notifications.notify({
                    title: 'ПВЗ отредактирован!',
                    type: 'success',
                })

                emit('refresh')
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При редактировании ПВЗ произошла ошбика',
                })
            } finally {
                state.is_loading = false
            }
        }

        const onCitySelect = () => {
            if (!state.city_id) return

            const { findObjectByValue } = useHelpers()
            const finded_city = findObjectByValue('id', state.city_id, data_state.cities.list)

            if (!finded_city) return
            state.city = { ...finded_city }

            // resetting address on city change
            state.full_address = null
            state.latitude = null
            state.longitude = null
        }

        const addPVZ = async () => {
            state_form$.value.$touch()
            if (state_form$.value.$invalid) return

            if (
                !state.name ||
                !state.latitude ||
                !state.longitude ||
                !state.phone ||
                !state.city_id ||
                !state.full_address ||
                !state.work_time_from ||
                !state.work_time_until
            )
                return

            try {
                state.is_loading = true

                const payload = {
                    id: state.id,
                    name: state.name,
                    latitude: state.latitude,
                    longitude: state.longitude,
                    phone: unmaskNumbers(state.phone),
                    city_id: state.city_id,
                    company_id: state.company_id,
                    full_address: state.full_address,
                    work_time_from: state.work_time_from,
                    work_time_until: state.work_time_until,
                    description: state.description,
                    is_active: state.is_active,
                }

                await pvz.createPVZ(payload)

                notifications.notify({
                    title: 'ПВЗ создан!',
                    type: 'success',
                })

                emit('refresh')
            } catch (error) {
                console.error(error)
                notifications.notifyError({ error, title: 'При создании ПВЗ произошла ошибка!' })
            } finally {
                state.is_loading = false
            }
        }

        const onSetAddress = ({
            address,
            street,
            latitude,
            longitude,
        }: {
            address: string
            street: string | null
            latitude: number
            longitude: number
        }) => {
            geocode_state.address = address
            geocode_state.street = street
            geocode_state.latitude = latitude
            geocode_state.longitude = longitude
        }

        const onStartGeocode = () => {
            state_form$.value.$reset()

            state_form$.value.city_id.$touch()
            if (state_form$.value.city_id.$error) return

            geocode_state.is_opened = true

            geocode_state.address = state.full_address
            geocode_state.street = state.street
            geocode_state.latitude = state.latitude
            geocode_state.longitude = state.longitude
        }

        const addAddress = () => {
            if (geocode_state.is_opened) {
                if (!geocode_state.address || !geocode_state.latitude || !geocode_state.longitude) {
                    return
                }

                state.full_address = geocode_state.address
                state.street = geocode_state.street
                state.latitude = geocode_state.latitude
                state.longitude = geocode_state.longitude

                geocode_state.address = null
                geocode_state.street = null
                geocode_state.is_opened = false

                return
            }
        }

        const addUser = async () => {
            state_form$.value.$reset()

            state_form$.value.$touch()
            if (state_form$.value.$invalid) return

            if (!props.initialValue.id || !state.user_id) return

            try {
                state.is_loading = true
                const form_data = new FormData()

                form_data.append('user_id', state.user_id.toString())

                await pvz.addUser(props.initialValue.id, form_data)

                notifications.notify({
                    title: 'Пользователь привязан к ПВЗ!',
                    type: 'success',
                })

                emit('refresh')
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При прикреплении пользователя к ПВЗ произошла ошибка!',
                })
            } finally {
                state.is_loading = false
            }
        }

        const clickHandler = () => {
            if (props.isCreate) addPVZ()
            else if (props.isUpdate) updatePVZInfo()
            else if (props.isAddUser) addUser()
        }

        // COMPANIES

        const loadCompanies = async (page: number, search?: string) => {
            data_state.companies.pagination.current_page = page

            try {
                data_state.companies.is_loading = true

                const params = {
                    page,
                    search,
                    limit: 9999,
                    statusId: 1,
                }

                const response = await companies.getAllList(params)

                data_state.companies.list = response.data
                data_state.companies.pagination.total_pages = response.meta.last_page

                data_state.companies.is_failed = false
                data_state.companies.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.companies.failure.catchError(error)

                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_failed = true
                }
            } finally {
                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_loading = false
                }
            }
        }

        const loadMoreCompanies = async (page: number) => {
            if (page > data_state.companies.pagination.total_pages) return
            data_state.companies.pagination.current_page = page

            try {
                data_state.companies.is_loading = true

                const params = {
                    page,
                    limit: 9999,
                    isPvz: 1,
                    statusId: 1,
                }

                const response = await companies.getAllList(params)
                data_state.companies.list = data_state.companies.list.concat(response.data)

                data_state.companies.pagination.total_pages = response.meta.last_page

                data_state.companies.is_failed = false
                data_state.companies.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.companies.failure.catchError(error)

                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_failed = true
                }
            } finally {
                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_loading = false
                }
            }
        }

        const searchCompany = async (search_query: string) => {
            // data_state.countries.pagination.current_page
            await loadCompanies(1, search_query)
        }

        const unshiftFindedCompany = async (company: CompanyT) => {
            await loadCompanies(data_state.companies.pagination.current_page)
            data_state.companies.list.unshift(company)
        }

        // CITIES

        const loadCities = async (page: number, search?: string) => {
            data_state.cities.pagination.current_page = page

            try {
                data_state.cities.is_loading = true

                const data = await cities.getAllList(page, search)
                data_state.cities.list = data.data

                data_state.cities.pagination.total_pages = data.meta.last_page

                data_state.cities.is_failed = false
                data_state.cities.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.cities.failure.catchError(error)

                if (!data_state.cities.failure.error_state.is_cancel) {
                    data_state.cities.is_failed = true
                }
            } finally {
                if (!data_state.cities.failure.error_state.is_cancel) {
                    data_state.cities.is_loading = false
                }
            }
        }

        const refreshCities = () => {
            loadCities(data_state.cities.pagination.current_page)
        }

        const loadMoreCities = async (page: number) => {
            if (page > data_state.cities.pagination.total_pages) return
            data_state.cities.pagination.current_page = page

            try {
                data_state.cities.is_loading = true

                const response = await cities.getAllList(page)
                data_state.cities.list = data_state.cities.list.concat(response.data)

                data_state.cities.pagination.total_pages = response.meta.last_page

                data_state.cities.is_failed = false
                data_state.cities.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.cities.failure.catchError(error)

                if (!data_state.cities.failure.error_state.is_cancel) {
                    data_state.cities.is_failed = true
                }
            } finally {
                if (!data_state.cities.failure.error_state.is_cancel) {
                    data_state.cities.is_loading = false
                }
            }
        }

        const searchCity = (search_query: string) => {
            // data_state.cities.pagination.current_page
            loadCities(1, search_query)
        }

        const unshiftFindedCity = async (city: CityT) => {
            await loadCities(data_state.cities.pagination.current_page)
            data_state.cities.list.unshift(city)
        }

        // USERS

        const loadUsers = async (page: number, search?: string) => {
            data_state.users.pagination.current_page = page

            try {
                data_state.users.is_loading = true

                const data = await users.getList({
                    page,
                    email: search,
                })

                data_state.users.list = data.data

                data_state.users.pagination.total_pages = data.meta.last_page

                data_state.users.is_failed = false
                data_state.users.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.users.failure.catchError(error)

                if (!data_state.users.failure.error_state.is_cancel) {
                    data_state.users.is_failed = true
                }
            } finally {
                if (!data_state.users.failure.error_state.is_cancel) {
                    data_state.users.is_loading = false
                }
            }
        }

        const refreshUsers = () => {
            loadUsers(data_state.users.pagination.current_page)
        }

        const loadMoreUsers = async (page: number) => {
            if (page > data_state.users.pagination.total_pages) return
            data_state.users.pagination.current_page = page

            try {
                data_state.users.is_loading = true

                const response = await users.getList({ page })
                data_state.users.list = data_state.users.list.concat(response.data)

                data_state.users.pagination.total_pages = response.meta.last_page

                data_state.users.is_failed = false
                data_state.users.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.users.failure.catchError(error)

                if (!data_state.users.failure.error_state.is_cancel) {
                    data_state.users.is_failed = true
                }
            } finally {
                if (!data_state.users.failure.error_state.is_cancel) {
                    data_state.users.is_loading = false
                }
            }
        }

        const searchUser = (search_query: string) => {
            // data_state.users.pagination.current_page
            loadUsers(1, search_query)
        }

        const unshiftFindedUser = async (user: UserT) => {
            await loadUsers(data_state.users.pagination.current_page)
            data_state.users.list.unshift(user)
        }

        // computed

        const modalTitle = computed(() => {
            return props.isAddUser
                ? 'Привязать пользователя'
                : props.initialValue
                ? 'Редактирование'
                : 'Добавление'
        })

        //

        onBeforeMount(() => {
            setPVZ()
            loadCities(1)
            loadUsers(1)
            loadCompanies(1)
        })

        return {
            state,
            geocode_state,
            state_form$,
            data_state,
            emitCancel,
            onDeletePVZ,
            onSetAddress,
            // USERS
            loadMoreUsers,
            refreshUsers,
            searchUser,
            unshiftFindedUser,
            // CITIES
            loadMoreCities,
            refreshCities,
            searchCity,
            unshiftFindedCity,
            // COMPANIES
            loadCompanies,
            loadMoreCompanies,
            searchCompany,
            unshiftFindedCompany,
            //
            onCitySelect,
            onStartGeocode,
            addAddress,
            clickHandler,
            // computed
            modalTitle,
            // constants
            PHONE_NUMBER_MASK,
        }
    },
})
